import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X, Settings2, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "hundify-cookie-consent-v1";

const DEFAULT_CONSENT = {
  essential: true,
  functional: false,
  analytics: false,
  decidedAt: null,
};

const CookieConsentContext = createContext(null);

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return { ...DEFAULT_CONSENT, ...parsed };
  } catch {
    return null;
  }
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [hasDecided, setHasDecided] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // hydrate from localStorage on mount
  useEffect(() => {
    const stored = readStored();
    if (stored && stored.decidedAt) {
      setConsent(stored);
      setHasDecided(true);
      setBannerOpen(false);
    } else {
      setBannerOpen(true);
    }
    setHydrated(true);
  }, []);

  const persist = (next) => {
    const value = { ...next, decidedAt: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* ignore quota errors */
    }
    setConsent(value);
    setHasDecided(true);
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  const api = useMemo(
    () => ({
      consent,
      hasDecided,
      openPreferences: () => setPrefsOpen(true),
      closePreferences: () => setPrefsOpen(false),
      acceptAll: () => persist({ essential: true, functional: true, analytics: true }),
      rejectNonEssential: () =>
        persist({ essential: true, functional: false, analytics: false }),
      savePreferences: (next) => persist({ essential: true, ...next }),
    }),
    [consent, hasDecided]
  );

  return (
    <CookieConsentContext.Provider value={api}>
      {children}

      {/* Banner */}
      {hydrated && bannerOpen && !prefsOpen && (
        <Banner
          onAcceptAll={api.acceptAll}
          onReject={api.rejectNonEssential}
          onCustomize={() => setPrefsOpen(true)}
        />
      )}

      {/* Preferences modal */}
      {prefsOpen && (
        <PreferencesModal
          initial={consent}
          onClose={() => {
            setPrefsOpen(false);
            if (!hasDecided) setBannerOpen(true);
          }}
          onSave={api.savePreferences}
        />
      )}
    </CookieConsentContext.Provider>
  );
}

function Banner({ onAcceptAll, onReject, onCustomize }) {
  return (
    <div
      data-testid="cookie-banner"
      role="region"
      aria-label="Cookie-meddelande"
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-[60] sm:max-w-md"
    >
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl p-5 sm:p-6 backdrop-blur-xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
              Vi använder cookies
            </h3>
            <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Vi använder nödvändiga cookies för att webbplatsen ska fungera samt
              valfria cookies för funktionalitet och anonym statistik. Läs mer i vår{" "}
              <a
                href="/cookies"
                className="text-emerald-600 dark:text-emerald-400 font-medium hover:underline"
              >
                cookiepolicy
              </a>
              .
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <Button
            data-testid="cookie-accept-all"
            onClick={onAcceptAll}
            className="col-span-2 h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-[0_6px_18px_-4px_rgba(16,185,129,0.55)]"
          >
            Acceptera alla
          </Button>
          <Button
            data-testid="cookie-reject"
            variant="outline"
            onClick={onReject}
            className="h-10 rounded-xl border-slate-300 dark:border-slate-700 font-medium"
          >
            Endast nödvändiga
          </Button>
          <Button
            data-testid="cookie-customize"
            variant="outline"
            onClick={onCustomize}
            className="h-10 rounded-xl border-slate-300 dark:border-slate-700 font-medium"
          >
            <Settings2 className="mr-2 h-4 w-4" />
            Inställningar
          </Button>
        </div>
      </div>
    </div>
  );
}

function PreferencesModal({ initial, onClose, onSave }) {
  const [functional, setFunctional] = useState(initial.functional);
  const [analytics, setAnalytics] = useState(initial.analytics);

  return (
    <div
      data-testid="cookie-preferences-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm"
    >
      <div className="w-full max-w-lg rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
        <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 p-6 text-white">
          <div className="absolute inset-0 bg-dotted opacity-20 pointer-events-none" />
          <button
            onClick={onClose}
            data-testid="cookie-preferences-close"
            aria-label="Stäng"
            className="absolute top-4 right-4 h-8 w-8 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-300">
                Cookie-inställningar
              </div>
              <h3 className="font-display text-xl font-bold mt-0.5">Anpassa ditt val</h3>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <PrefRow
            title="Nödvändiga"
            desc="Krävs för att webbplatsen ska fungera (inloggning, säkerhet, formulär). Kan inte stängas av."
            checked
            disabled
            testid="pref-essential"
          />
          <PrefRow
            title="Funktionella"
            desc="Sparar dina inställningar som tema (ljust/mörkt) och språkval för en bättre upplevelse."
            checked={functional}
            onChange={setFunctional}
            testid="pref-functional"
          />
          <PrefRow
            title="Analys"
            desc="Anonym statistik om hur webbplatsen används så att vi kan förbättra den."
            checked={analytics}
            onChange={setAnalytics}
            testid="pref-analytics"
          />

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-xl h-11"
              data-testid="cookie-preferences-cancel"
            >
              Avbryt
            </Button>
            <Button
              onClick={() => onSave({ functional, analytics })}
              data-testid="cookie-preferences-save"
              className="rounded-xl h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex-1"
            >
              Spara mina val
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-4">
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 dark:text-white text-sm flex items-center gap-2">
          {title}
          {disabled && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
              Alltid på
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
          {desc}
        </p>
      </div>
      <Switch
        data-testid={testid}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </div>
  );
}
